import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { dauer } from '../lib/bericht'
import { ZUSTAND } from '../lib/cmdb'
import { formatDateTime } from '../lib/format'
import type { AlarmAssistenz, AlarmNotiz, FruehereAlarme } from '../lib/types'
import { ErrorBox, Loading } from './Feedback'

/** „3-mal in 12 Monaten, meist nach 20 min vorbei“. */
export function haeufigkeit(f: FruehereAlarme): string {
  if (f.anzahl === 0) return 'noch nie'
  const mal = f.anzahl === 1 ? 'einmal' : `${f.anzahl}-mal`
  const dauerText = f.mittlere_dauer_s !== null ? `, im Mittel nach ${dauer(f.mittlere_dauer_s)} vorbei` : ''
  return `${mal} in den letzten 12 Monaten${dauerText}`
}

/**
 * Alarm-Assistent (Stufe 5.5): was die Netzwarte über diesen Alarm weiß –
 * frühere Alarme derselben Ursache, was damals geholfen hat, was rundherum
 * passiert ist, welche Dienste betroffen sind, und Erfahrungswissen. Er
 * nutzt nur eigene Daten; nichts verlässt die Plattform.
 */
export function AlarmAssistent({ alertId, darfNotieren }: { alertId: string; darfNotieren: boolean }) {
  const hilfe = useQuery({
    queryKey: ['alarm-assistent', alertId],
    queryFn: ({ signal }) => apiGet<AlarmAssistenz>(`/alerts/${alertId}/assistent`, signal),
  })
  if (hilfe.isPending) return <Loading what="Den Assistenten" />
  if (hilfe.isError) return <ErrorBox error={hilfe.error} what="Der Assistent" />
  const h = hilfe.data

  return (
    <div className="assistent" aria-label="Assistent">
      <div className="assistent-spalten">
        <div>
          <h3 className="assistent-titel">Erfahrung: {h.erfahrung.thema}</h3>
          {h.erfahrung.ursachen.length > 0 ? (
            <>
              <div className="muted">Häufige Ursachen</div>
              <ul>
                {h.erfahrung.ursachen.map((u) => (
                  <li key={u}>{u}</li>
                ))}
              </ul>
            </>
          ) : null}
          <div className="muted">Zuerst prüfen</div>
          <ol>
            {h.erfahrung.schritte.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        </div>

        <div>
          <h3 className="assistent-titel">Früher</h3>
          <p>
            An diesem Gerät: {haeufigkeit(h.dieses_geraet)}.
            <br />
            Gleiche Ursache an anderen Geräten: {haeufigkeit(h.gleiche_ursache)}
            {h.gleiche_ursache.geraete > 1 ? ` (${h.gleiche_ursache.geraete} Geräte)` : ''}.
          </p>
          {h.loesungen.length > 0 ? (
            <>
              <div className="muted">Was geholfen hat</div>
              <ul>
                {h.loesungen.map((n) => (
                  <li key={n.id}>
                    „{n.text}“
                    <div className="muted" style={{ fontSize: 12 }}>
                      {n.geraet ? `${n.geraet} · ` : ''}
                      {n.von} · {formatDateTime(n.zeit)}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="muted">Noch keine Lösung festgehalten.</p>
          )}
        </div>

        <div>
          <h3 className="assistent-titel">Zusammenhang</h3>
          {h.zusammenhang.length === 0 && h.dienste.length === 0 && h.meldungen.length === 0 ? (
            <p className="muted">Nichts Auffälliges rundherum.</p>
          ) : null}
          {h.zusammenhang.length > 0 ? (
            <ul>
              {h.zusammenhang.map((z) => (
                <li key={`${z.art}-${z.text}`}>
                  {z.text}
                  {z.zeit ? <span className="muted"> · {formatDateTime(z.zeit)}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}
          {h.dienste.length > 0 ? (
            <>
              <div className="muted">Betroffene Dienste</div>
              <ul>
                {h.dienste.map((d) => (
                  <li key={d.dienst_id}>
                    {d.dienst} <span className={ZUSTAND[d.bei_ausfall].klasse}>{ZUSTAND[d.bei_ausfall].text}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {(h.eskalation ?? []).length > 0 ? (
            <>
              <div className="muted">Eskaliert, weil niemand bestätigt hat</div>
              <ul>
                {(h.eskalation ?? []).map((e) => (
                  <li key={e.stufe}>
                    Stufe {e.stufe} an {e.kanal} <span className="muted">· {formatDateTime(e.zeit)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {h.meldungen.length > 0 ? (
            <>
              <div className="muted">Meldungen des Geräts um diese Zeit</div>
              <ul className="mono" style={{ fontSize: 12 }}>
                {h.meldungen.map((m) => (
                  <li key={m.id}>
                    {formatDateTime(m.time)} {m.message}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </div>

      <Notizen alertId={alertId} notizen={h.notizen} darf={darfNotieren} />
      <p className="muted" style={{ fontSize: 12, margin: 0 }}>
        Der Assistent nutzt nur Daten dieser Netzwarte; es geht nichts an externe Dienste.
      </p>
    </div>
  )
}

function Notizen({ alertId, notizen, darf }: { alertId: string; notizen: AlarmNotiz[]; darf: boolean }) {
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const [loesung, setLoesung] = useState(false)
  const speichern = useMutation({
    mutationFn: () => apiSend<AlarmNotiz>('POST', `/alerts/${alertId}/notizen`, { text: text.trim(), loesung }),
    onSuccess: () => {
      setText('')
      setLoesung(false)
      void queryClient.invalidateQueries({ queryKey: ['alarm-assistent'] })
    },
  })
  return (
    <div>
      <h3 className="assistent-titel">Notizen zu diesem Alarm</h3>
      {notizen.length > 0 ? (
        <ul>
          {notizen.map((n) => (
            <li key={n.id}>
              {n.loesung ? <span className="badge badge-ok">Lösung</span> : null} {n.text}
              <span className="muted">
                {' '}
                · {n.von} · {formatDateTime(n.zeit)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {darf ? (
        <form
          className="row"
          style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault()
            speichern.mutate()
          }}
        >
          <div className="field grow">
            <label htmlFor={`notiz-${alertId}`}>Notiz</label>
            <input
              id={`notiz-${alertId}`}
              className="input"
              maxLength={2000}
              placeholder="Was wurde getan, was hat geholfen?"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <label className="row" style={{ gap: 6 }}>
            <input type="checkbox" checked={loesung} onChange={(e) => setLoesung(e.target.checked)} />
            Das hat geholfen
          </label>
          <button type="submit" className="btn btn-sm" disabled={speichern.isPending || !text.trim()}>
            Notiz speichern
          </button>
          {speichern.isError ? (
            <span style={{ color: 'var(--crit)' }}>{(speichern.error as Error).message}</span>
          ) : null}
        </form>
      ) : null}
    </div>
  )
}
